import { QueryKey } from "@tanstack/react-query";
import { z } from "zod";

import { ApiHost } from "../api-client";
import { NetworkingLayerConfig } from "../config";
import {
  SSEEventSchemasMap,
  SSEParsedEvent,
  SSEReconnectOptions,
  useOnStreamEvents,
  UseSSEStreamResult,
} from "../use-sse-stream";

import { createMutationBaseMixin, MutationBaseMixin } from "./mixins/base-mutation";
import { createQueryBaseMixin, QueryBaseMixin } from "./mixins/base-query";
import {
  openSSEStreamConnection,
  type OpenSSEStreamConnectionOptions,
  type OpenSSEStreamConnectionResult,
} from "./open-sse-stream-connection";
import {
  type CallerCombinedVariables,
  type MutationBodySchema,
  MutationDefinitionOptions,
  QueryDefinitionOptions,
} from "./types";

interface SSEStreamDefinitionOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
> {
  host: ApiHost;
  path: Path;
  paramsSchema?: ParamsSchema;
  headers?: Record<string, string>;
  reconnect?: SSEReconnectOptions;
  events: EventSchemas;
}

interface SSEStreamDefinition<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
> {
  openStreamConnection: (
    options: CallerCombinedVariables<Path, ParamsSchema> &
      OpenSSEStreamConnectionOptions<EventSchemas>
  ) => OpenSSEStreamConnectionResult;
  useOnStreamEvents: (
    options: CallerCombinedVariables<Path, ParamsSchema> & {
      headers?: Record<string, string>;
      enabled?: boolean;
      reconnect?: SSEReconnectOptions;
      onEvent: (event: SSEParsedEvent<EventSchemas>) => void;
      onError?: (error: Error) => void;
      sessionStorageKey?: string;
    }
  ) => UseSSEStreamResult;
}

export interface NetworkingLayerDefinitions<DelegatedErrorParams> {
  createQueryRequestDefinition: <
    TQueryKey extends QueryKey,
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: QueryDefinitionOptions<
      TQueryKey,
      Path,
      ParamsSchema,
      ResponseSchema,
      TransformedResult
    >
  ) => QueryBaseMixin<TQueryKey, Path, ParamsSchema, ResponseSchema, TransformedResult>;

  createMutationRequestDefinition: <
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    BodySchema extends MutationBodySchema = z.ZodVoid,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: MutationDefinitionOptions<
      Path,
      ParamsSchema,
      ResponseSchema,
      BodySchema,
      TransformedResult
    >
  ) => MutationBaseMixin<Path, ParamsSchema, BodySchema, TransformedResult, DelegatedErrorParams>;

  createSSEStreamDefinition: <
    Path extends string,
    ParamsSchema extends z.ZodTypeAny = z.ZodVoid,
    EventSchemas extends SSEEventSchemasMap = SSEEventSchemasMap,
  >(
    definitionOptions: SSEStreamDefinitionOptions<Path, ParamsSchema, EventSchemas>
  ) => SSEStreamDefinition<Path, ParamsSchema, EventSchemas>;
}

export function createQueryRequestDefinitions<DelegatedErrorParams = unknown>(
  config: NetworkingLayerConfig
): NetworkingLayerDefinitions<DelegatedErrorParams> {
  function createQueryRequestDefinition<
    TQueryKey extends QueryKey,
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: QueryDefinitionOptions<
      TQueryKey,
      Path,
      ParamsSchema,
      ResponseSchema,
      TransformedResult
    >
  ): QueryBaseMixin<TQueryKey, Path, ParamsSchema, ResponseSchema, TransformedResult> {
    return createQueryBaseMixin(config, definitionOptions);
  }

  function createMutationRequestDefinition<
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    BodySchema extends MutationBodySchema = z.ZodVoid,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: MutationDefinitionOptions<
      Path,
      ParamsSchema,
      ResponseSchema,
      BodySchema,
      TransformedResult
    >
  ): MutationBaseMixin<Path, ParamsSchema, BodySchema, TransformedResult, DelegatedErrorParams> {
    return createMutationBaseMixin<
      DelegatedErrorParams,
      Path,
      ParamsSchema,
      ResponseSchema,
      BodySchema,
      TransformedResult
    >(config, definitionOptions);
  }

  function createSSEStreamDefinition<
    Path extends string,
    ParamsSchema extends z.ZodTypeAny = z.ZodVoid,
    EventSchemas extends SSEEventSchemasMap = SSEEventSchemasMap,
  >(
    definitionOptions: SSEStreamDefinitionOptions<Path, ParamsSchema, EventSchemas>
  ): SSEStreamDefinition<Path, ParamsSchema, EventSchemas> {
    function openStreamConnection(
      options: CallerCombinedVariables<Path, ParamsSchema> &
        OpenSSEStreamConnectionOptions<EventSchemas>
    ) {
      const pathParams = "pathParams" in options ? options.pathParams : undefined;
      const params = "params" in options ? options.params : undefined;
      const parsedParams = definitionOptions.paramsSchema
        ? definitionOptions.paramsSchema.parse(params)
        : (params as Record<string, unknown>);

      return openSSEStreamConnection(
        config,
        {
          host: definitionOptions.host,
          path: definitionOptions.path,
          pathParams,
          params: parsedParams,
          headers: definitionOptions.headers,
          reconnect: definitionOptions.reconnect,
          events: definitionOptions.events,
        },
        options
      );
    }

    function useOnStreamEventsHook(
      options: CallerCombinedVariables<Path, ParamsSchema> & {
        headers?: Record<string, string>;
        enabled?: boolean;
        reconnect?: SSEReconnectOptions;
        onEvent: (event: SSEParsedEvent<EventSchemas>) => void;
        onError?: (error: Error) => void;
        sessionStorageKey?: string;
      }
    ) {
      const pathParams = "pathParams" in options ? options.pathParams : undefined;
      const params = "params" in options ? options.params : undefined;

      return useOnStreamEvents({
        config,
        host: definitionOptions.host,
        path: definitionOptions.path,
        pathParams,
        paramsSchema: definitionOptions.paramsSchema,
        events: definitionOptions.events,
        params,
        enabled: options.enabled,
        reconnect: options.reconnect ?? definitionOptions.reconnect,
        headers: {
          ...definitionOptions.headers,
          ...options.headers,
        },
        onEvent: options.onEvent,
        onError: options.onError,
        sessionStorageKey: options.sessionStorageKey,
      });
    }

    return {
      openStreamConnection,
      useOnStreamEvents: useOnStreamEventsHook,
    };
  }

  return {
    createQueryRequestDefinition,
    createMutationRequestDefinition,
    createSSEStreamDefinition,
  };
}
